/**
 * TanStack Router beforeLoad Factories
 *
 * Creates beforeLoad handlers for authentication and permission checks.
 * These run BEFORE the route renders, blocking unauthorized navigation.
 *
 * Key concepts:
 * - beforeLoad runs sequentially (parent → child)
 * - Use for auth checks and redirects (keep thin, no heavy data fetching)
 * - Throw redirect() to block navigation
 * - Return context object to pass data to child routes
 *
 * @module router/authBeforeLoad
 */

import { redirect } from '@tanstack/react-router';
import { getAuthStateSync } from '../utils/authState';
import { validateRedirectUrl } from './redirectUtils';

/**
 * @typedef {Object} AuthContext
 * @property {string} token - JWT access token
 * @property {Set<string>} permissions - User permissions
 */

/**
 * @typedef {Object} AuthBeforeLoadOptions
 * @property {string} [loginPath='/login'] - Path to redirect unauthenticated users
 */

/**
 * Factory for creating beforeLoad that ensures authentication.
 * Use on a layout route to protect all children.
 *
 * @param {AuthBeforeLoadOptions} [options] - Configuration options
 * @returns {Function} beforeLoad handler
 *
 * @example
 * ```js
 * import { createRoute } from '@tanstack/react-router';
 * import { createAuthBeforeLoad } from '@bb/session-manager';
 *
 * // Create an authenticated layout route
 * const authLayoutRoute = createRoute({
 *   getParentRoute: () => rootRoute,
 *   id: 'authenticated',
 *   beforeLoad: createAuthBeforeLoad({ loginPath: '/bbweb/login' }),
 *   component: AuthenticatedLayout,
 * });
 *
 * // All child routes are now protected
 * const homeRoute = createRoute({
 *   getParentRoute: () => authLayoutRoute,
 *   path: '/home',
 *   component: HomePage,
 * });
 * ```
 */
export function createAuthBeforeLoad(options = {}) {
  const { loginPath = '/login' } = options;

  /**
   * @param {Object} ctx - Route context from TanStack Router
   * @param {Object} ctx.location - Current location object
   * @param {string} ctx.location.href - Full URL path
   * @returns {Promise<AuthContext>} Auth context for child routes
   * @throws {redirect} If user is not authenticated
   */
  return async ({ location }) => {
    // Check if this is an OIDC callback URL (returning from identity provider)
    // If so, skip auth check and let the OIDC library process the callback first
    const searchParams = new URLSearchParams(location.searchStr || '');
    const hasOidcCallback = searchParams.has('code') && searchParams.has('state');

    if (hasOidcCallback) {
      console.debug(
        '[BB:Router] OIDC callback detected, skipping auth check to allow token exchange'
      );
      // Return empty context - the OIDC library will handle authentication
      return { token: null, permissions: new Set() };
    }

    // Synchronous check - fast path, avoids stale closure issues
    const { isAuthenticated, token, permissions } = getAuthStateSync();
    console.debug('[BB:Router] Auth beforeLoad check:', {
      path: location.href,
      isAuthenticated,
      hasToken: !!token,
      permissionCount: permissions?.size || 0,
    });

    if (!isAuthenticated || !token) {
      // Skip redirect param when already on the login page to prevent
      // /login?redirect=%2Flogin loops after re-login.
      const isAlreadyOnLogin =
        location.href?.endsWith('/login') || location.pathname?.endsWith('/login');
      if (isAlreadyOnLogin) {
        console.debug(
          '[BB:Router] Already on login page, redirecting without redirect param'
        );
        throw redirect({ to: loginPath });
      }

      // Validate redirect URL to prevent open redirect attacks
      const safeRedirect = validateRedirectUrl(location.href);
      console.debug(
        '[BB:Router] Not authenticated, redirecting to login with redirect:',
        safeRedirect
      );

      throw redirect({
        to: loginPath,
        search: { redirect: safeRedirect },
      });
    }

    // Return context for child routes to access
    return { token, permissions };
  };
}

/**
 * @typedef {Object} PermissionBeforeLoadOptions
 * @property {string[]} permissions - Required permissions
 * @property {boolean} [requireAll=false] - Require ALL permissions (AND) vs ANY (OR)
 * @property {string} [forbiddenPath='/home'] - Path for unauthorized users
 */

/**
 * Factory for creating beforeLoad that checks permissions.
 * Expects parent route to have run createAuthBeforeLoad first.
 *
 * @param {PermissionBeforeLoadOptions} options - Configuration options
 * @returns {Function} beforeLoad handler
 *
 * @example
 * ```js
 * import { createRoute } from '@tanstack/react-router';
 * import { createPermissionBeforeLoad } from '@bb/session-manager';
 *
 * // Require ANY of the listed permissions (OR logic)
 * const adminRoute = createRoute({
 *   getParentRoute: () => authLayoutRoute,
 *   path: '/admin',
 *   beforeLoad: createPermissionBeforeLoad({
 *     permissions: ['admin', 'companyadmin'],
 *     forbiddenPath: '/bbweb/home',
 *   }),
 *   component: AdminPage,
 * });
 *
 * // Require ALL permissions (AND logic)
 * const superAdminRoute = createRoute({
 *   getParentRoute: () => authLayoutRoute,
 *   path: '/super-admin',
 *   beforeLoad: createPermissionBeforeLoad({
 *     permissions: ['admin', 'superuser'],
 *     requireAll: true,
 *     forbiddenPath: '/bbweb/home',
 *   }),
 *   component: SuperAdminPage,
 * });
 * ```
 */
export function createPermissionBeforeLoad(options) {
  const { permissions, requireAll = false, forbiddenPath = '/home' } = options;

  /**
   * @param {Object} ctx - Route context from TanStack Router
   * @param {AuthContext} ctx.context - Auth context from parent route
   * @returns {Promise<AuthContext>} Passed through auth context
   * @throws {redirect} If user lacks required permissions
   */
  return async ({ context }) => {
    const { permissions: userPermissions } = context;

    // Check if userPermissions is a Set (from parent auth route)
    if (!userPermissions || !(userPermissions instanceof Set)) {
      console.warn(
        '[Auth] createPermissionBeforeLoad requires parent route to use createAuthBeforeLoad'
      );
      throw redirect({ to: forbiddenPath });
    }

    const hasAccess = requireAll
      ? permissions.every((p) => userPermissions.has(p))
      : permissions.some((p) => userPermissions.has(p));

    if (!hasAccess) {
      throw redirect({ to: forbiddenPath });
    }

    // Pass through context for further child routes
    return context;
  };
}

/**
 * Combine multiple permission checks.
 * Useful for routes that need complex permission logic.
 *
 * @param {...Function} beforeLoadFns - beforeLoad functions to combine
 * @returns {Function} Combined beforeLoad handler
 *
 * @example
 * ```js
 * const complexRoute = createRoute({
 *   getParentRoute: () => authLayoutRoute,
 *   path: '/complex',
 *   beforeLoad: combineBeforeLoad(
 *     createPermissionBeforeLoad({ permissions: ['feature-x'] }),
 *     customFeatureFlagCheck,
 *   ),
 *   component: ComplexPage,
 * });
 * ```
 */
export function combineBeforeLoad(...beforeLoadFns) {
  return async (ctx) => {
    let context = ctx.context || {};

    for (const fn of beforeLoadFns) {
      const result = await fn({ ...ctx, context });
      if (result && typeof result === 'object') {
        context = { ...context, ...result };
      }
    }

    return context;
  };
}
